import React from "react";

// One-page A4 resume, only rendered when printing (see CV.css)

const PHONE = "+33 6 48 41 71 69";

const contact = [
  PHONE,
  "hello@quentinrobert.com",
  "linkedin.com/in/quentin-robert-a94311274",
  "github.com/Firfal",
  "quentinrobert.com/cv",
].filter(Boolean);

const experience = [
  {
    title: "Co-founder & CTO",
    org: "Lidap",
    meta: "lidap.io · Paris",
    dates: "2022 – Present",
    bullets: [
      "Designed, built and shipped an invoicing SaaS for freelancers end to end, from UX to production; first paying customers in Oct. 2025.",
      "Cut invoice creation time from 1 min 30 to 45 s (-50 %) with smart client search and saved services.",
      "Stack: React, TypeScript, Firebase (Auth, Firestore, Storage), Tailwind, Recharts.",
    ],
  },
  {
    title: "Freelance Developer",
    org: "Automations, internal tools & websites",
    meta: "Remote · Paris",
    dates: "2023 – Present",
    bullets: [
      "Built a WhatsApp Business campaign manager and shared inbox: Next.js 15, Cloud Functions, WhatsApp Cloud API webhooks.",
      "Shipped Forma Host, an online course platform in production for Ecole Motion: Stripe Connect, Vimeo, push notifications, CI/CD with GitHub Actions.",
      "Migrated Webflow sites to Firebase Hosting (scraper, link checker in CI, forms on Firestore); built sites for Lovis, Pin-Ads and Veiga Motion.",
    ],
  },
];

const projects = [
  {
    title: "Pullback",
    meta: "3rd place, screenpipe hackathon · Swift, Claude API, screenpipe, MCP",
    text: "Native macOS app that reads your local screenpipe memory, flags what you left unfinished with verbatim proof from the capture, and launches a Claude agent to finish it (web, Mail, Calendar, browser, MCP tools); anything leaving the Mac waits for your approval. getpullback.com",
  },
  {
    title: "OpenFlow",
    meta: "Open-source · TypeScript, Next.js, MCP, Firebase",
    text: "Sites generated by Claude Code that owners edit visually or through their own AI via a per-site MCP server (OAuth). AI-written code is checked against a verifiable standard with an automatic feedback loop, so the agent fixes its own deviations.",
  },
  {
    title: "NestAI Agent",
    meta: "Hackathon winner · Dify, OpenAI, OpenStreetMap",
    text: "Real-estate agent built in 48 h: pulls live listings, scores each property 0–100 from nearby amenities with parallel analysis and custom Dify plugins.",
  },
  {
    title: "Appart Hunter",
    meta: "Next.js, Cloud Run Jobs, Playwright, Leaflet",
    text: "Multi-source apartment listing collector (Bien'ici, Leboncoin) running every 5 min, with geo scoring, map view and web push alerts to be first on every listing.",
  },
  {
    title: "Max Jeune trip planner",
    meta: "Live · TypeScript, Next.js, Cloud Run, Firestore",
    text: "Routing engine over SNCF Open Data that chains connections, verifies return trips and searches over date ranges; nightly ingestion and golden tests.",
  },
];

const skills = [
  { title: "AI & agents", items: "Claude Code, MCP servers, OpenAI API, Dify, n8n, prompt & agent workflows" },
  { title: "Languages", items: "TypeScript, JavaScript, C, C++, Python" },
  { title: "Frontend", items: "React, Next.js, Tailwind CSS, shadcn/ui" },
  { title: "Backend", items: "Firebase (Firestore, Auth, Functions), Cloud Run, Stripe, REST & webhooks" },
  { title: "Tooling", items: "Git, GitHub Actions, Playwright, Webflow, Airtable, Zapier" },
];

const awards = [
  { title: "3rd place, Track 2", sub: "screenpipe × 42 Entrepreneurs hackathon · Oct. 2026" },
  { title: "Winner, Dify challenge", sub: "{Tech: Europe} Paris AI Hack · 2026" },
  { title: "Pitch to 80 builders", sub: "MuleRun × Paatch builder night · 2026" },
  { title: "Mindly, HR wellbeing app", sub: "Miro Hackathon · 2026" },
];

function Heading({ children }) {
  return (
    <h2 className="font-cabinet font-black text-[14px] uppercase tracking-wide border-b border-secondary/40 pb-0.5 mb-1.5">
      {children}
    </h2>
  );
}

export default function PrintResume() {
  return (
    <div className="hidden print:block w-full text-secondary text-[11.5px] leading-[1.45]">
      <header className="flex justify-between items-end gap-6 pb-3 mb-3 border-b-2 border-secondary">
        <div>
          <h1 className="font-cabinet font-black text-[34px] leading-none">Quentin Robert</h1>
          <p className="font-cabinet font-bold text-[16px] mt-1">AI Engineer</p>
        </div>
        <div className="text-right text-[11px]">
          <p className="font-semibold">Paris, France · Available now</p>
          <p>Internship · Full-time · Freelance</p>
        </div>
      </header>
      <p className="text-[11px] mb-3">{contact.join("  ·  ")}</p>

      <div className="grid grid-cols-[1fr_205px] gap-6">
        <div className="flex flex-col gap-4">
          <section>
            <Heading>Profile</Heading>
            <p>
              AI engineer who ships: I build AI agents, MCP servers and the full-stack products around them, from prototype to production.
              Co-founder & CTO of a SaaS with paying customers, 3rd at the screenpipe hackathon and winner of the Dify challenge at {"{Tech: Europe}"} Paris AI Hack,
              and student at 42 Paris. Comfortable owning a product end to end: architecture, UI, backend, payments, CI/CD.
            </p>
          </section>

          <section>
            <Heading>Experience</Heading>
            <div className="flex flex-col gap-2">
              {experience.map((e) => (
                <div key={e.title} className="break-inside-avoid">
                  <div className="flex justify-between items-baseline">
                    <p><span className="font-bold text-[12.5px]">{e.title}</span> · <span className="font-semibold">{e.org}</span></p>
                    <p className="font-semibold shrink-0">{e.dates}</p>
                  </div>
                  <p className="text-[10.5px] opacity-70">{e.meta}</p>
                  <ul className="list-disc pl-4 mt-0.5">
                    {e.bullets.map((b) => <li key={b}>{b}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          <section>
            <Heading>Projects</Heading>
            <div className="flex flex-col gap-2">
              {projects.map((p) => (
                <div key={p.title} className="break-inside-avoid">
                  <p><span className="font-bold text-[12.5px]">{p.title}</span> <span className="text-[10.5px] opacity-70">· {p.meta}</span></p>
                  <p>{p.text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="flex flex-col gap-4">
          <section>
            <Heading>Skills</Heading>
            <div className="flex flex-col gap-1.5">
              {skills.map((s) => (
                <div key={s.title}>
                  <p className="font-bold">{s.title}</p>
                  <p>{s.items}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <Heading>Education</Heading>
            <p className="font-bold">42 Paris</p>
            <p className="font-semibold">Software engineering</p>
            <p className="font-semibold">Nov. 2025 – Present</p>
            <p>Project-based, peer-to-peer curriculum. Common core in progress: libft, ft_printf, get_next_line, minishell (C), C++ modules.</p>
          </section>

          <section>
            <Heading>Awards & hackathons</Heading>
            <div className="flex flex-col gap-1">
              {awards.map((a) => (
                <div key={a.title}>
                  <p className="font-bold">{a.title}</p>
                  <p className="text-[10.5px]">{a.sub}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <Heading>Languages</Heading>
            <p>French · Native</p>
            <p>English · Professional</p>
          </section>

          <section>
            <Heading>Community</Heading>
            <p>
              Regular at Paris AI builder events: Lovable × Photoroom Buildathon, MCP Connect with OpenAI @leboncoin, Hack the Gap × BLACKBOX.AI, n8n Meetup Paris, Paris Claude Founder House.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
